/**
 * Baseline Compliance Page — Carbon (frontend-carbon-blueprint §5.2,
 * mockup .agents/Mockups/black-redesign-2026-10/carbon/02-baseline-drift.html).
 *
 * Composition (top to bottom):
 *   1. PillarBrief — the action-first verdict, one lede, the tertiary
 *      "Export baseline report", provenance, and the Baseline Compliance
 *      score tile (swatch, numeral, delta tag).
 *   2. PillarTabs — Drifts / Monitoring / Export as line tabs, no counts (the
 *      queue's own tabs count drifts). (The PR Tracker tab was retired with
 *      the TCM cutover — see the TabKey note below.)
 *   3. Per-tab body:
 *      - Drifts:  the Drifts DataTable (DSC or Hybrid; rows expand in place),
 *                 then the 30-day drift trend tile.
 *      - Export:  BaselineExportTab under a section heading.
 *
 * Hard-preserved (regression surface): drift status / workload / severity
 * filters, drift detail, approve/waive/commit actions, PR links,
 * CSV/HTML export downloads, tab URL sync (?tab=…), tenant context auto-sync
 * from the sidebar, "Start new scan" controls.
 *
 * Defer audit (per posture-pages-redesign.md §2.1):
 *   - Per-workload baseline contribution → swapped for the four-component
 *     Guardian Score composite (real values, no fabricated weighting).
 *   - "21 April" anomaly marker on the trend chart → no anomaly model exists.
 *   - "Lifts baseline by ~N pts" sub-clauses → no impact model, so none drawn.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useSearchParams } from 'react-router-dom'
import { format } from 'date-fns'

import { api, tcmApi } from '@/lib/api'
import { useTenantContext } from '@/hooks/useTenantContext'
import {
  calculateStarredBpaScore,
  useComplianceScores,
} from '@/hooks/useComplianceData'
import { useComplianceBrief } from '@/hooks/useComplianceBrief'
import { summarizeDriftTrend } from '@/lib/derive-trend-stats'
import { knownBaselinePct } from '@/components/Dashboard/v2/cc-score'
import { baselineUnknownReason } from '@/components/BaselineCompliance/baseline-unknown'
import { getWorkloadForDriftComponent } from '@/lib/workloadMapping'
import { driftResourceName } from '@/lib/drift-names'
import { lastAssessmentAt } from '@/lib/tenant-assessed'
import { canEditScanSchedule } from '@/lib/scan-schedule'
import { useTenantScanSchedule } from '@/hooks/useTenantScanSchedule'
import { type ConfigurationDrift, DriftSeverity, type M365Workload } from '@/types'

import PillarBrief from '@/components/PostureBrief/PillarBrief'
import PillarTabs, {
  usePillarTab,
  type TabConfig,
} from '@/components/PostureBrief/PillarTabs'
import { SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import { ButtonLink } from '@/components/carbon/Button'
import { IconDownload } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { ScoreTile } from '@/components/carbon/ScoreTile'
import { Tile } from '@/components/carbon/Tile'
import { TrendChart } from '@/components/carbon/TrendChart'

import {
  DriftManagementTab,
  HybridDriftManagementTab,
  MonitoringTab,
  BaselineExportTab,
} from '@/components/BaselineCompliance'
import BaselineWelcome from '@/components/BaselineCompliance/BaselineReviewCeremony'

// NOTE: The end-user-facing "PR Tracker" tab was retired now that the fleet is
// on TCM — a DSC-baseline-PR view that only ever covered SPO/OneDrive (and
// never TCM-owned workloads) read as confusing/incomplete. The DSC SPO/ODB →
// Azure DevOps baseline-PR mechanics still run server-side; only the UI surface
// is gone. TODO (low priority): tear out the now-dead PR-tracker code path —
// see TODO.md "Remove PR Tracker code".
type TabKey = 'drifts' | 'monitoring' | 'export'

const SEVERITY_RANK: Record<DriftSeverity, number> = {
  [DriftSeverity.Critical]: 0,
  [DriftSeverity.High]:     1,
  [DriftSeverity.Medium]:   2,
  [DriftSeverity.Low]:      3,
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function pluralize(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function ConfigurationDrift() {
  const [searchParams] = useSearchParams()
  const { hash } = useLocation()
  const { defaultTenantId, isGlobalAdmin, isLoading: isLoadingTenant, userTenants } =
    useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined)

  // Initialize selectedTenantId from context once loaded. Same precedence as
  // the legacy page so deep-linked ?tenant=… continues to override.
  useEffect(() => {
    if (isLoadingTenant) return
    const tenantParam = searchParams.get('tenant')
    if (tenantParam) {
      setSelectedTenantId(tenantParam)
      return
    }
    if (defaultTenantId) {
      setSelectedTenantId(defaultTenantId)
    } else if (!isGlobalAdmin && userTenants.length > 0) {
      setSelectedTenantId(userTenants[0].tenantId)
    }
  }, [isLoadingTenant, defaultTenantId, isGlobalAdmin, userTenants, searchParams])

  /* ---------------- Data queries ---------------- */

  // DashboardStats: drives compliance score, severity counts, drift trends,
  // active drift count, recent activity. Same key as the Command Center to
  // share cache.
  const { data: dashboardStats, isLoading: statsLoading, isError: statsError } = useQuery({
    queryKey: ['dashboard', selectedTenantId],
    queryFn: () =>
      selectedTenantId ? api.getDashboardStats(selectedTenantId) : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // Score-history feed for the 30-day score delta (drives the score tile's
  // delta tag and the brief's verdict). Slow-changing → 5 minute stale.
  const { data: scoreHistory } = useQuery({
    queryKey: ['compliance-score-history-30d', selectedTenantId],
    queryFn: () => api.getComplianceScoreHistory(selectedTenantId!, 30),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
  })

  // Guardian Score components (Baseline 20 / BPA 30 / MFA 20 / Secure Score 30).
  // Wired identically to Dashboard.tsx so the score box renders the same
  // composite the Command Center shows.
  const { data: secureScoreSummary, isLoading: secureScoreLoading } = useQuery({
    queryKey: ['secure-score-summary', selectedTenantId],
    queryFn: () => api.getSecureScoreSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: userInsights, isLoading: userInsightsLoading } = useQuery({
    queryKey: ['user-insights', selectedTenantId],
    queryFn: () => api.getUserInsights(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: complianceScores, isLoading: complianceScoresLoading } =
    useComplianceScores(selectedTenantId)

  // WO-9 (reworked): first-run welcome brief. A born-Hybrid tenant's first
  // visit to the drift page shows a one-time "here's your starting point"
  // brief instead of the queue. It gates nothing — the queue and every count
  // are live from day 1; the CTA records an acknowledgment and continues.
  const baselineSource =
    userTenants.find(t => t.tenantId === selectedTenantId)?.baselineSource ?? 'DSC'
  const isHybrid = baselineSource === 'Hybrid'

  // OB-03: a self-serve tenant still in ExoProvisioning isn't Hybrid yet, so it
  // gets the DSC queue below, which can't show its drift, and nothing on this
  // tab said why. The coverage overlay (same cache key as the Hybrid queue's
  // mail banner) names the state; read-only, and a failed fetch omits the notice.
  const { data: coverage } = useQuery({
    queryKey: ['tcm-coverage', selectedTenantId],
    queryFn: () => tcmApi.getCoverage(selectedTenantId!),
    enabled: !!selectedTenantId && baselineSource === 'DSC',
    refetchOnWindowFocus: false,
  })
  const mailCoverageIncomplete =
    baselineSource === 'DSC' && coverage?.onboarding?.exoProvisioning === true

  const { data: reviewStatus } = useQuery({
    queryKey: ['baseline-review', selectedTenantId],
    queryFn: () => api.getBaselineReviewStatus(selectedTenantId!),
    // Gate on Hybrid: a DSC tenant never has a first-run welcome, so we skip
    // the probe entirely (the endpoint would return welcomePending=false anyway).
    enabled: !!selectedTenantId && isHybrid,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const welcomePending = !!reviewStatus?.welcomePending

  // The signed-in user's role gates the never-scanned brief's action (Platform
  // Admin, schedule editor, member). Shares the cache key useTenantContext
  // already populates.
  const { data: currentUser, isLoading: currentUserLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: api.getCurrentUser,
    staleTime: 5 * 60 * 1000,
  })

  // The provenance line's time (D36): the tenant's last real assessment, the
  // same stamp the header's "Last assessed" reads. Null for a never-scanned
  // tenant (its creation stamp is no scan), which reads "Awaiting first assessment".
  const assessedAt = lastAssessmentAt(userTenants.find(t => t.tenantId === selectedTenantId))

  // Local dismissal so the queue appears the instant the acknowledgment lands,
  // without waiting on the 60s-stale welcome probe to refetch. Keyed by tenant
  // id — a plain boolean would leak one tenant's dismissal across the tenant
  // switcher and silently suppress the next born-Hybrid tenant's one-time
  // brief. The server-side stamp is what keeps the brief from ever returning.
  const queryClient = useQueryClient()
  const [dismissedWelcomeTenant, setDismissedWelcomeTenant] = useState<string | null>(null)

  // Mirrors Dashboard.tsx — uses `weightedScore` so the composite-bar tile
  // matches the BPA pillar's hero metric (which sources `weightedScore` via
  // `transformToDisplayFrameworks`).
  const bpaStarredScore = useMemo(() => {
    if (!complianceScores?.standardScores) return undefined
    const mapped = complianceScores.standardScores.map(s => ({
      code: s.standardCode,
      score: s.weightedScore,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
    }))
    return calculateStarredBpaScore(mapped) ?? undefined
  }, [complianceScores?.standardScores])

  /* ---------------- Derivations ---------------- */

  // Stabilise the array reference so summarizeDriftTrend downstream doesn't
  // re-fire on every render. Without this the `?? []` fallback creates a
  // fresh literal on each call and the deps array sees a different identity
  // every time.
  const driftTrends = useMemo(
    () => dashboardStats?.driftTrends ?? [],
    [dashboardStats?.driftTrends],
  )
  const activeDriftCount = dashboardStats?.activeDrifts ?? 0

  const sev = dashboardStats?.issuesBySeverity
  const critical = sev?.critical ?? 0
  const high = sev?.high ?? 0

  // 30-day pillar score delta — first → last point of the 30d overall
  // history. `getComplianceScoreHistory` already normalises to a sorted
  // `ComplianceTrendPoint[]` (oldest → newest).
  //
  // We only surface a trend once the history actually spans ~the full window.
  // A freshly-onboarded tenant's earliest point is its cold-start evaluation
  // (collectors not yet populated → an artificially low score), so diffing the
  // current score against it manufactures a large "+N pts" jump that reflects
  // data filling in, not a real 30-day improvement — confusing for brand-new
  // tenants who've never scanned. Requiring the span to reach the window means
  // that cold-start point has aged out of the 30-day query before we draw any
  // trend; below that we suppress the chip entirely (see `hideDelta`). When we
  // do draw one we label it with the *actual* elapsed span, not a hardcoded 30.
  const scoreTrend = useMemo(() => {
    const NONE = { value: 0, days: 30, hasTrend: false }
    if (!Array.isArray(scoreHistory) || scoreHistory.length < 2) return NONE
    const first = scoreHistory[0]
    const last = scoreHistory[scoreHistory.length - 1]
    const firstScore = first?.overallScore ?? null
    const lastScore = last?.overallScore ?? null
    if (firstScore == null || lastScore == null) return NONE
    const spanDays = Math.round(
      (new Date(last.date).getTime() - new Date(first.date).getTime()) / 86_400_000,
    )
    // ~the full 30d window, less a 2-day tolerance for a missing edge point.
    const MIN_TREND_DAYS = 28
    if (!Number.isFinite(spanDays) || spanDays < MIN_TREND_DAYS) return NONE
    return {
      value: Math.round((lastScore - firstScore) * 10) / 10,
      days: spanDays,
      hasTrend: true,
    }
  }, [scoreHistory])

  // The drift the brief leads with: the server's critical/high detail list
  // (DashboardStats.criticalDrift), severity-ranked then most-recent. On Hybrid
  // tenants `criticalDrift` is the DSC + TCM union from
  // ComplianceService.GetCriticalDriftAsync, so TCM-owned workloads
  // (Entra/EXO/Intune/Teams/Defender/Purview) surface here — not just DSC
  // SPO/OneDrive. It's the same dashboard payload that drives the severity
  // counts, so the brief and the counts can't diverge. The server caps at 10
  // and (on the Hybrid path) sorts by severity; we re-sort defensively because
  // the DSC-only path orders by DetectedAt alone.
  const topDrifts = useMemo<ConfigurationDrift[]>(() => {
    const items = dashboardStats?.criticalDrift ?? []
    return [...items]
      .sort((a, b) => {
        const sevDelta = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9)
        if (sevDelta !== 0) return sevDelta
        return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
      })
      .slice(0, 3)
  }, [dashboardStats?.criticalDrift])

  // Distinct workloads carrying critical/high drift — feeds the brief's
  // headline and lede. Maps each drift's component to the canonical workload
  // bucket (Identity / Exchange / Teams / SharePoint / Defender) and dedupes,
  // so the brief names real workloads instead of resource names.
  // `getWorkloadForDriftComponent` handles both DSC PascalCase components and
  // TCM dotted resource types (microsoft.entra.* etc.), so on Hybrid tenants
  // TCM-owned workloads get named here too — keeping the verdict honest. Sourced
  // from the same `criticalDrift` union as `topDrifts`. `Other` is dropped —
  // naming it reads as filler (TCM Purview has no workload bucket and lands here).
  const driftingWorkloads = useMemo(() => {
    const items = dashboardStats?.criticalDrift ?? []
    const set = new Set<M365Workload>()
    for (const d of items) {
      const wl = getWorkloadForDriftComponent(d.component)
      if (wl !== 'Other') set.add(wl)
    }
    return Array.from(set)
  }, [dashboardStats?.criticalDrift])

  // Brief composition. Score = REAL pillar percentage (DashboardStats), delta
  // = 30d derived above, workload list flows into the headline and lede,
  // critical count drives the count-driven fallback.
  // Unknown is never zero: no checks run (unscanned tenant), a failed stats
  // call, or a baseline still being captured all leave the score unknown.
  const baselineUnknown = baselineUnknownReason(dashboardStats, {
    loading: statsLoading,
    failed: statsError,
  })
  const baselinePct = baselineUnknown ? null : knownBaselinePct(dashboardStats)
  // Nothing scanned, or the stats call failed: no counts, and none of the
  // actions that need data behind them (the report export, the queue triage).
  const countsUnknown = baselineUnknown === 'unscanned' || baselineUnknown === 'failed'
  // Only a never-scanned tenant's queue says there is no baseline data yet.
  // After a failed stats call the drift list (its own endpoint) still loads,
  // so an empty queue there is real.
  const unscanned = baselineUnknown === 'unscanned'
  // Only a Platform Admin can run a scan (Ad-Hoc Scans; the route and the
  // scan endpoint both gate on it). Anyone else learns what turns the first
  // assessment on (finding 5), from the tenant's schedule: a viewer who may
  // edit it gets Tenant Management once it is known off. The role fails
  // closed while /me is unknown. Same hook as the Command Center.
  const canRunScans = currentUser?.isPlatformAdmin === true
  const scanSchedule = useTenantScanSchedule({
    tenantId: selectedTenantId,
    neverScanned: unscanned,
    currentUser,
    currentUserLoading,
  })
  const scheduleSettingsHref = unscanned && !canRunScans && canEditScanSchedule(currentUser) ? '/tenants' : undefined

  const brief = useComplianceBrief({
    tenantId: selectedTenantId,
    score: baselinePct,
    // §5.2 wiring note (post-2a): the lede's resource count, only when the
    // stats payload actually carried it. Never the coalesced 0 below
    // (`activeDriftCount`), which a failed call would render as an all-clear.
    driftedResources: dashboardStats?.activeDriftsKnown ? dashboardStats.activeDrifts : undefined,
    // The drift queue's anchor; the effect below scrolls <main> to it.
    primaryHref: '#drift-queue',
    // With the null score above, the brief says why the score is unknown and what to do next.
    noData: baselineUnknown ?? undefined,
    firstScanHref: canRunScans ? '/admin/scans' : undefined,
    scheduleSettingsHref,
    scanSchedule,
    assessedAt,
    resourcesChecked: dashboardStats?.complianceScore?.totalChecks,
    deltas: scoreTrend.hasTrend
      ? { score: scoreTrend.value, days: scoreTrend.days }
      : undefined,
    criticalCount: critical,
    driftingWorkloads,
    topAction: topDrifts[0]
      ? {
          pillar: 'compliance',
          // Setting-shaped top action: the resource's readable name and the
          // drifted property, passed separately so the composer writes the
          // sentence ("Start with the Auto-forward enabled setting."; it reads
          // the property through settingLabel, as the drift rows do).
          // The " · " label join stays in the queue tooltip; it is not prose.
          title: driftResourceName(topDrifts[0].component),
          setting: topDrifts[0].setting,
          impact:
            topDrifts[0].severity === DriftSeverity.Critical
              ? 'crit'
              : topDrifts[0].severity === DriftSeverity.High
                ? 'high'
                : 'med',
        }
      : undefined,
  })

  // Guardian Score composite — same four bars the Command Center shows. We
  // intentionally use the SAME bar mix on every Posture page (per §2.1
  // "Replace with the four-component composite from the Command Center").
  const compositeBars = useMemo(
    () => [
      {
        label: 'Baseline drift',
        weight: '20%',
        value: Math.round(baselinePct ?? 0),
        fillClass: 'bg-cat-baseline',
      },
      {
        label: 'Best practice',
        weight: '30%',
        value: Math.round(bpaStarredScore ?? 0),
        fillClass: 'bg-cat-bpa',
      },
      {
        label: 'MFA adoption',
        weight: '20%',
        value: Math.round(userInsights?.mfaMemberAdoptionPercentage ?? 0),
        fillClass: 'bg-cat-identity',
      },
      {
        label: 'M365 Secure Score',
        weight: '30%',
        value: Math.round(secureScoreSummary?.score?.percentage ?? 0),
        fillClass: 'bg-cat-secure',
      },
    ],
    [
      baselinePct,
      bpaStarredScore,
      userInsights?.mfaMemberAdoptionPercentage,
      secureScoreSummary?.score?.percentage,
    ],
  )

  const briefLoading =
    statsLoading || secureScoreLoading || userInsightsLoading || complianceScoresLoading

  /* ---------------- Tabs ---------------- */

  // No count on the Drifts tab: the only page-level figure, `activeDrifts`,
  // counts resources with drift, while the queue's Open tab right below counts
  // drifts (one per drifted setting), and a tab count can't name its unit.
  // The brief's lede carries the resource count, named as resources.
  const tabs = useMemo<ReadonlyArray<TabConfig<TabKey>>>(
    () => [
      { id: 'drifts',     label: 'Drifts' },
      // Coverage transparency + setup journey (Monitoring UX plan §3) —
      // intentionally badge-free: it's an explainer, not a work queue.
      { id: 'monitoring', label: 'Monitoring' },
      { id: 'export',     label: 'Export' },
    ],
    [],
  )

  // The page composes a single source of truth for the active tab. PillarTabs
  // also calls usePillarTab internally for the URL sync; the validIds list is
  // identical so both stay aligned.
  const [activeTab] = usePillarTab(
    tabs.map(t => t.id),
    'drifts',
  )

  // The brief's tertiary (mockup 02) opens the Export tab, keeping the rest of
  // the query string (?tenant=…) so the deep link survives.
  const exportSearch = useMemo(() => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'export')
    return `?${next.toString()}`
  }, [searchParams])

  // The mail-coverage notice's action opens the Monitoring tab, where the setup
  // journey carries the run-2 step for Exchange.
  const monitoringSearch = useMemo(() => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'monitoring')
    return `?${next.toString()}`
  }, [searchParams])

  // Hash-deep-link from the Operations heatmap (`#full-queue`) and from the brief's
  // primary button (`#drift-queue`; a router hash link doesn't scroll on its own). The section
  // only mounts on the Drifts tab and the brief above it shifts vertical
  // position once data lands, so we re-scroll after `briefLoading` flips
  // false to land on the right pixel.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const target = hash === '#drift-queue' ? 'drift-queue' : hash === '#full-queue' ? 'full-queue' : null
    if (!target) return
    if (activeTab !== 'drifts') return
    if (briefLoading) return
    const id = window.requestAnimationFrame(() => {
      document.getElementById(target)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
    return () => window.cancelAnimationFrame(id)
  }, [activeTab, briefLoading, hash])

  /* ---------------- Render ---------------- */

  return (
    <div className="min-w-0">
      <PillarBrief
        brief={brief}
        score={baselinePct}
        components={compositeBars}
        tone="compliance"
        isLoading={briefLoading}
        scoreLabel="Baseline Compliance"
        hideDelta={!scoreTrend.hasTrend || baselineUnknown != null}
        aside={
          baselineUnknown ? (
            <ScoreTile
              title="Baseline Compliance"
              category="baseline"
              score={null}
              meta="No data"
              className="max-c-lg:max-w-[640px]"
            />
          ) : undefined
        }
        secondaryAction={
          activeTab === 'export' || countsUnknown ? undefined : (
            <ButtonLink to={{ search: exportSearch }} kind="tertiary" icon={<IconDownload />}>
              Export baseline report
            </ButtonLink>
          )
        }
      />

      <PillarTabs
        tabs={tabs}
        defaultTab="drifts"
        tone="compliance"
        ariaLabel="Baseline compliance view"
      />

      {activeTab === 'drifts' && (
        // WO-9 (reworked): a born-Hybrid tenant that hasn't acknowledged its
        // first-run welcome sees the one-time brief; everyone else gets the
        // normal drift queue. The brief gates nothing — its CTA records the
        // acknowledgment and drops straight into the queue.
        welcomePending && selectedTenantId && dismissedWelcomeTenant !== selectedTenantId ? (
          <BaselineWelcome
            tenantId={selectedTenantId}
            assessedAt={assessedAt}
            onContinue={() => {
              setDismissedWelcomeTenant(selectedTenantId)
              void queryClient.invalidateQueries({ queryKey: ['baseline-review', selectedTenantId] })
            }}
          />
        ) : (
          <DriftsTabPanel
            tenantId={selectedTenantId}
            // Wave 7: drive ManagementTab selection from the tenant's
            // BaselineSource. Defaults to 'DSC' so every existing tenant
            // renders the legacy DriftManagementTab.
            baselineSource={baselineSource}
            activeDriftCount={activeDriftCount}
            driftTrends={driftTrends}
            countsUnknown={countsUnknown}
            unscanned={unscanned}
            severityCounts={{ critical, high }}
            mailCoverageIncomplete={mailCoverageIncomplete}
            monitoringSearch={monitoringSearch}
          />
        )
      )}

      {activeTab === 'monitoring' && (
        <div id="panel-monitoring" role="tabpanel" aria-labelledby="tab-monitoring">
          {/* MonitoringTab owns its own sections (Coverage / Setup journey /
              What we watch / How decisions work), so no wrapper here. */}
          <MonitoringTab tenantId={selectedTenantId} />
        </div>
      )}

      {activeTab === 'export' && (
        <section id="panel-export" role="tabpanel" aria-labelledby="tab-export">
          <SectionHeadline level="page">Take the record with you.</SectionHeadline>
          <p className="m-0 mt-2 max-w-[56ch] text-body text-text-secondary">
            Download your baseline configuration report as CSV or HTML. The drift audit history exports from the
            Drifts tab.
          </p>
          <div className="mt-6">
            <BaselineExportTab tenantId={selectedTenantId} />
          </div>
        </section>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Drifts tab body                                                            */
/* -------------------------------------------------------------------------- */

interface DriftsTabPanelProps {
  tenantId: string | undefined
  /**
   * Wave 7 — branch the queue tab on the tenant's `BaselineSource`.
   * 'DSC' (default) renders the DSC DriftManagementTab;
   * 'Hybrid' renders HybridDriftManagementTab (merges TCM + DSC).
   * 'TCM' (reserved) currently falls through to Hybrid until the pure-
   * TCM tab ships in a later wave.
   */
  baselineSource: string
  /** `DashboardStats.activeDrifts`: resources with drift, not drifts. */
  activeDriftCount: number
  driftTrends: { date: string; critical: number; high: number; medium: number; low: number; info: number }[]
  severityCounts: { critical: number; high: number }
  /** Nothing scanned or the stats call failed: no zeros, no trend sentence. */
  countsUnknown?: boolean
  /** Nothing scanned: the queue's empty Open view says there is no baseline data yet. */
  unscanned?: boolean
  /**
   * OB-03: the tenant is still ExoProvisioning (mail coverage unfinished), so it
   * isn't Hybrid and this DSC queue can't show its drift. Shows the next step.
   */
  mailCoverageIncomplete?: boolean
  /** Query string that opens the Monitoring tab, for the notice's action. */
  monitoringSearch?: string
}

/**
 * The Drifts tab, in mockup 02's order: the queue (status tabs, toolbar,
 * expandable rows) straight under the brief, then the 30-day drift trend as a
 * tile below it. The old stats strip and "Top items" table are gone: the
 * counts are on the queue's tabs and the Command Center, and the queue itself
 * now leads with the most severe drifts.
 */
function DriftsTabPanel({
  tenantId,
  baselineSource,
  activeDriftCount,
  driftTrends,
  severityCounts,
  countsUnknown = false,
  unscanned = false,
  mailCoverageIncomplete = false,
  monitoringSearch = '?tab=monitoring',
}: DriftsTabPanelProps) {
  // The 30-day series: the per-day total open-drift count (sum of severity
  // buckets) — same shape PillarCompliance uses.
  const trendPoints = useMemo(
    () =>
      (countsUnknown ? [] : driftTrends).map(t => ({
        x: formatTrendDate(t.date),
        y: (t.critical ?? 0) + (t.high ?? 0) + (t.medium ?? 0) + (t.low ?? 0) + (t.info ?? 0),
      })),
    [driftTrends, countsUnknown],
  )

  // Verdict + window/week totals for the chart heading. Returns null when the
  // trend series is empty, so the chart falls back to the "The trend fills in
  // as Guardian records drift for this tenant." empty state instead of
  // pretending the math worked.
  const trendSummary = useMemo(
    () => (countsUnknown ? null : summarizeDriftTrend(driftTrends)),
    [driftTrends, countsUnknown],
  )

  return (
    <div id="panel-drifts" role="tabpanel" aria-labelledby="tab-drifts" className="flex flex-col gap-12">
      {/* The full queue. Wave 7: branch on BaselineSource. 'DSC' (default)
          renders the DSC tab; 'Hybrid' renders the merged TCM + DSC view. */}
      <section id="full-queue" aria-label="The full queue" className="scroll-mt-24">
        <p className="sr-only">
          {countsUnknown
            ? 'Open drifts ranked by severity.'
            : `Open drifts ranked by severity: ${severityCounts.critical} critical and ${severityCounts.high} high, across ${activeDriftCount} ${pluralize(activeDriftCount, 'resource', 'resources')} with drift.`}
        </p>
        {baselineSource === 'DSC' ? (
          <>
            {mailCoverageIncomplete ? (
              <InlineNotification
                kind="info"
                title="Finish mail coverage to open this queue."
                className="mb-6"
                actions={
                  <ButtonLink to={{ search: monitoringSearch }} kind="tertiary" size="sm">
                    Finish setup
                  </ButtonLink>
                }
              >
                Your Microsoft 365 admin needs to run the setup script once more to finish Exchange. Until then,
                this queue can&rsquo;t show drift for this tenant.
              </InlineNotification>
            ) : null}
            <DriftManagementTab tenantId={tenantId} countsUnknown={countsUnknown} unscanned={unscanned} />
          </>
        ) : (
          <HybridDriftManagementTab tenantId={tenantId} countsUnknown={countsUnknown} unscanned={unscanned} />
        )}
      </section>

      {/* Drift trend, 30 days, below the queue. The heading names the verdict,
          the line under it carries the actual counts so the chart never goes
          silent on numbers, and the chart's hover readout gives each day's
          count. Both numbers come from the existing DashboardStats.driftTrends
          payload — no new endpoint. */}
      <section aria-label="Drift trend">
        <Tile padding={24}>
          <SectionHeadline level="page">
            {trendSummary ? trendHeadline(trendSummary) : 'Drift trend, 30 days.'}
          </SectionHeadline>
          <p className="m-0 mt-2 max-w-[64ch] text-body text-text-secondary">
            {trendSummary ? (
              <>
                {trendSummary.windowTotal > 0 ? (
                  <>
                    <b className="font-semibold tabular-nums text-text-primary">{trendSummary.windowTotal}</b>{' '}
                    {pluralize(trendSummary.windowTotal, 'drift', 'drifts')} opened across the last 30 days.{' '}
                  </>
                ) : (
                  <>No new drift opened across the last 30 days. </>
                )}
                {activeDriftCount > 0 ? (
                  // `activeDrifts` counts resources, not drifts: name the unit.
                  <>
                    <b className="font-semibold tabular-nums text-text-primary">{activeDriftCount}</b>{' '}
                    {pluralize(activeDriftCount, 'resource still has', 'resources still have')} open drift.
                  </>
                ) : (
                  <>Nothing is open today.</>
                )}
                {trendSummary.direction != null && <> {weekOverWeek(trendSummary)}</>}
              </>
            ) : (
              <>New drifts opened per day, summed across workloads.</>
            )}
          </p>
          <div className="mt-6">
            {trendPoints.length === 0 ? (
              <p className="m-0 flex h-[220px] items-center justify-center bg-layer-2 text-helper text-text-helper">
                The trend fills in as Guardian records drift for this tenant.
              </p>
            ) : (
              <TrendChart
                height={220}
                series={[{ id: 'opened', label: 'Drifts opened', category: 'baseline', points: trendPoints }]}
                label={
                  trendSummary
                    ? `Drifts opened per day over 30 days: ${trendSummary.windowTotal} in total.`
                    : 'Drifts opened per day over 30 days.'
                }
              />
            )}
          </div>
        </Tile>
      </section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The trend heading. Directional verdicts from `summarizeDriftTrend` carry the
 * real 7-day count; the two fixed verdicts keep their wording.
 */
function trendHeadline(
  summary: { verdict: string; direction: 'rising' | 'easing' | 'flat' | null; recentTotal: number; windowTotal: number },
): string {
  if (summary.windowTotal > 0 && summary.direction === 'easing') return `Drift down to ${summary.recentTotal} this week.`
  if (summary.windowTotal > 0 && summary.direction === 'rising') return `Drift up to ${summary.recentTotal} this week.`
  if (summary.windowTotal > 0 && summary.direction === 'flat') return `Drift holding at ${summary.recentTotal} this week.`
  if (summary.windowTotal === 0) return 'No new drift in the last 30 days.'
  return summary.verdict
}

/**
 * Week-over-week clause for the line under the trend heading: "Down from 23
 * last week." or "Up from 9 last week.". Null when there's no direction to
 * talk about (handled by the caller).
 */
function weekOverWeek(
  summary: { direction: 'rising' | 'easing' | 'flat' | null; recentTotal: number; priorTotal: number },
): React.ReactNode {
  if (summary.direction === null) return null
  if (summary.direction === 'flat') return 'Holding flat versus last week.'
  const verb = summary.direction === 'easing' ? 'Down from' : 'Up from'
  return (
    <>
      {verb} <b className="font-semibold tabular-nums text-text-primary">{summary.priorTotal}</b> last week.
    </>
  )
}

/** "May 9" for a trend point; the raw value when the API hands back something unparseable. */
function formatTrendDate(iso: string): string {
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? iso : format(parsed, 'MMM d')
}
